import { useState, useRef, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import {
  Upload,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  Download,
  FileUp,
  Eye,
  Table as TableIcon,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SAMPLE_CSV = `student_id,first_name,last_name,grade_level,email,driveline_tag,barcode,f1g1_parent_phone,f1g1_parent_email
100001,Emma,Johnson,7,emma.johnson@iltexasstudent.org,412,BC100001,972-555-1234,parent.johnson@gmail.com
100002,Liam,Garcia,6,liam.garcia@iltexasstudent.org,213,BC100002,972-555-5678,parent.garcia@gmail.com
`;

const HEADER_ALIASES = {
  id: "student_id",
  studentid: "student_id",
  first: "first_name",
  firstname: "first_name",
  last: "last_name",
  lastname: "last_name",
  grade: "grade_level",
  gradelevel: "grade_level",
  studentemail: "email",
  email_address: "email",
  drivelinetag: "driveline_tag",
  carrider: "driveline_tag",
  car_rider_tag: "driveline_tag",
  f1g1_phone: "f1g1_parent_phone",
  f1g1phone: "f1g1_parent_phone",
  f1g2_phone: "f1g2_parent_phone",
  f2g1_phone: "f2g1_parent_phone",
  f1g1_email: "f1g1_parent_email",
  f2g1_email: "f2g1_parent_email",
};

const REQUIRED_ALWAYS = ["student_id", "first_name", "last_name"];

const normalizeHeader = (h) => {
  const cleaned = h.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
  return HEADER_ALIASES[cleaned] || cleaned;
};

// Simple CSV parser that handles quoted fields and either , or \t delimiters
const splitLine = (line, delim) => {
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (c === '"') {
        inQ = false;
      } else {
        cur += c;
      }
    } else {
      if (c === '"') {
        inQ = true;
      } else if (c === delim) {
        out.push(cur);
        cur = "";
      } else {
        cur += c;
      }
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
};

const parseRaw = (raw) => {
  const lines = raw.replace(/\r\n/g, "\n").split("\n").filter((l) => l.trim());
  if (lines.length === 0) return { headers: [], rows: [], delim: "," };
  const firstLine = lines[0];
  const tabCount = (firstLine.match(/\t/g) || []).length;
  const commaCount = (firstLine.match(/,/g) || []).length;
  const delim = tabCount >= commaCount ? "\t" : ",";
  const headers = splitLine(firstLine, delim).map(normalizeHeader);
  const rows = lines.slice(1).map((line) => {
    const values = splitLine(line, delim);
    const obj = {};
    headers.forEach((h, i) => (obj[h] = values[i] ?? ""));
    return obj;
  });
  return { headers, rows, delim };
};

// Build a clean CSV string (comma-delimited, quoted) to send to backend
const rowsToCSV = (headers, rows) => {
  const escape = (v) => {
    const s = String(v ?? "");
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const out = [headers.map(escape).join(",")];
  for (const row of rows) {
    out.push(headers.map((h) => escape(row[h] ?? "")).join(","));
  }
  return out.join("\n");
};

export default function BulkImportStudents({ onClose, onSuccess }) {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [importData, setImportData] = useState("");
  const [requireEmail, setRequireEmail] = useState(false);
  const [requireBarcode, setRequireBarcode] = useState(false);
  const fileInputRef = useRef(null);

  const parsed = useMemo(() => (importData.trim() ? parseRaw(importData) : { headers: [], rows: [] }), [importData]);

  const requiredCols = useMemo(() => {
    const req = [...REQUIRED_ALWAYS];
    if (requireEmail) req.push("email");
    if (requireBarcode) req.push("barcode");
    return req;
  }, [requireEmail, requireBarcode]);

  const missingHeaders = useMemo(
    () => requiredCols.filter((c) => !parsed.headers.includes(c)),
    [requiredCols, parsed.headers],
  );

  // Row-level issues (only relevant if the required headers exist)
  const rowIssues = useMemo(() => {
    if (!parsed.rows.length) return [];
    const issues = [];
    parsed.rows.forEach((row, idx) => {
      const missing = [];
      for (const c of requiredCols) {
        if (parsed.headers.includes(c)) {
          const v = (row[c] || "").trim();
          if (!v) missing.push(c);
        }
      }
      if (missing.length) issues.push({ line: idx + 2, sid: row.student_id, missing });
    });
    return issues;
  }, [parsed, requiredCols]);

  const validRows = useMemo(
    () => parsed.rows.filter((_, idx) => !rowIssues.find((i) => i.line === idx + 2)),
    [parsed.rows, rowIssues],
  );

  const canUpload = parsed.rows.length > 0 && missingHeaders.length === 0 && validRows.length > 0 && !processing;

  const doUpload = async () => {
    if (!canUpload) return;
    setProcessing(true);
    setError(null);
    setResult(null);
    try {
      const csv = rowsToCSV(parsed.headers, validRows);
      const blob = new Blob([csv], { type: "text/csv" });
      const fd = new FormData();
      fd.append("file", blob, "roster.csv");
      const res = await axios.post(`${BACKEND_URL}/api/students/bulk-upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const merged = {
        ...res.data,
        rejected_client_side: rowIssues.length,
      };
      setResult(merged);
      if (res.data?.added > 0) {
        setTimeout(() => onSuccess?.(), 3000);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || err.message || "Upload failed.");
    } finally {
      setProcessing(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setResult(null);
    const text = await file.text();
    setImportData(text);
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "student_roster_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const previewRows = parsed.rows.slice(0, 5);
  const displayCols = parsed.headers.slice(0, 8);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <Card className="w-full max-w-4xl my-8">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Bulk Import Students
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose} disabled={processing}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-5">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {result && (
            <Alert className="bg-green-50 text-green-900 border-green-200">
              <CheckCircle className="w-4 h-4" />
              <AlertDescription>
                <p className="font-semibold">Import complete</p>
                <p className="text-sm mt-1">
                  Added <strong>{result.added}</strong> new students, skipped{" "}
                  <strong>{result.skipped}</strong> duplicates
                  {result.rejected_client_side > 0 && (
                    <>
                      , rejected <strong>{result.rejected_client_side}</strong> rows with missing required fields
                    </>
                  )}
                  .
                </p>
                <p className="text-xs mt-2 text-green-800">
                  Every student can now sign in with their Student ID and{" "}
                  <code className="px-1 bg-green-100 rounded">E@gles26!</code>
                </p>
              </AlertDescription>
            </Alert>
          )}

          <Alert className="bg-blue-50 border-blue-200">
            <AlertDescription className="text-sm space-y-1 text-blue-900">
              <p><strong>Required columns:</strong> student_id, first_name, last_name</p>
              <p><strong>Optional columns:</strong> grade_level, email, driveline_tag, barcode, f1g1_parent_phone, f1g1_parent_email, f2g1_parent_phone, f2g1_parent_email</p>
              <p className="text-xs text-blue-800">Accepts tab-separated (from Excel/Sheets) or comma-separated (CSV). Duplicate student_ids are skipped.</p>
            </AlertDescription>
          </Alert>

          {/* Required-field toggles */}
          <div className="flex flex-col sm:flex-row gap-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="flex items-center gap-3">
              <Switch id="req-email" checked={requireEmail} onCheckedChange={setRequireEmail} />
              <label htmlFor="req-email" className="text-sm font-medium text-slate-700 cursor-pointer">
                Require email
              </label>
            </div>
            <div className="flex items-center gap-3">
              <Switch id="req-barcode" checked={requireBarcode} onCheckedChange={setRequireBarcode} />
              <label htmlFor="req-barcode" className="text-sm font-medium text-slate-700 cursor-pointer">
                Require barcode
              </label>
            </div>
            <p className="text-xs text-slate-500 sm:ml-auto sm:self-center">
              Rows missing required fields will be rejected before upload.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={downloadSample}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV template
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={processing}
            >
              <FileUp className="w-4 h-4 mr-2" />
              Upload CSV file
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.tsv,.txt"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Or paste your data below</p>
            <Textarea
              value={importData}
              onChange={(e) => {
                setImportData(e.target.value);
                setError(null);
                setResult(null);
              }}
              placeholder={"student_id,first_name,last_name,grade_level\n100001,Emma,Johnson,7\n100002,Liam,Garcia,6"}
              className="h-40 font-mono text-xs"
              disabled={processing}
            />
          </div>

          {/* PREVIEW */}
          {parsed.rows.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-semibold text-slate-700">Preview</span>
                  <span className="text-xs text-slate-500">
                    (showing {previewRows.length} of {parsed.rows.length} rows)
                  </span>
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-600">
                  <TableIcon className="w-3.5 h-3.5" />
                  {parsed.headers.length} columns detected
                </div>
              </div>

              {missingHeaders.length > 0 && (
                <div className="px-4 py-3 bg-red-50 border-b border-red-200 text-sm text-red-800">
                  <p className="font-semibold flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4" />
                    Missing required column{missingHeaders.length === 1 ? "" : "s"}:{" "}
                    <code className="bg-red-100 px-1.5 py-0.5 rounded">{missingHeaders.join(", ")}</code>
                  </p>
                  <p className="text-xs mt-1">Add these columns to your header row and re-paste.</p>
                </div>
              )}

              {rowIssues.length > 0 && missingHeaders.length === 0 && (
                <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 text-sm text-amber-900">
                  <p className="font-semibold flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4" />
                    {rowIssues.length} row{rowIssues.length === 1 ? "" : "s"} will be rejected (missing required fields)
                  </p>
                  <details className="text-xs mt-1">
                    <summary className="cursor-pointer">Show details</summary>
                    <ul className="mt-1 space-y-0.5 list-disc list-inside max-h-40 overflow-y-auto">
                      {rowIssues.slice(0, 50).map((iss) => (
                        <li key={iss.line}>
                          Line {iss.line} {iss.sid ? `(ID ${iss.sid})` : ""}: missing {iss.missing.join(", ")}
                        </li>
                      ))}
                      {rowIssues.length > 50 && <li>...and {rowIssues.length - 50} more</li>}
                    </ul>
                  </details>
                </div>
              )}

              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      {displayCols.map((c) => (
                        <th key={c} className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap border-b border-slate-200">
                          {c}
                          {requiredCols.includes(c) && <span className="text-red-500 ml-0.5">*</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                        {displayCols.map((c) => (
                          <td
                            key={c}
                            className={`px-3 py-2 whitespace-nowrap ${
                              requiredCols.includes(c) && !row[c]?.trim()
                                ? "bg-red-50 text-red-700 font-medium"
                                : "text-slate-700"
                            }`}
                          >
                            {row[c]?.trim() || <span className="text-slate-300 italic">empty</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-600 flex items-center justify-between">
                <span>
                  {validRows.length} valid row{validRows.length === 1 ? "" : "s"} ready to upload
                </span>
                <span>{parsed.headers.length} columns</span>
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={processing}>
              Close
            </Button>
            <Button
              onClick={doUpload}
              disabled={!canUpload}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Import {validRows.length} Student{validRows.length === 1 ? "" : "s"}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
